// Settings > Picking > Experiments — the walk-order experiment.
//
// The arms are DATA, not code. 'standard' (the live walk_order) and
// 'corridor' (a derived facing-pair order) are built in; every other arm is a
// walk order the operator downloaded, re-numbered in a spreadsheet, and
// uploaded back as A, B, C... Any two or more of them can run at once, and a
// wave is assigned an arm at build time and keeps it for life.
//
// The question the panel answers is "how much faster or slower is each of
// these than what we run today?", so the delta against the baseline is the
// headline and everything else supports it. Three deliberate choices:
//
//   • The delta is ALWAYS shown, including on twelve waves. Hiding it until
//     some significance bar is cleared just means the operator goes and
//     computes it themselves from worse data. The `confidence` grade
//     qualifies the number instead.
//   • The primary metric is seconds per MOVE (a pick at a different location
//     from the last one), not overall pick rate. Total pick speed is
//     dominated by handling time, which the walk order does not touch — a
//     real travel gain can hide inside it for days. Units/hour is shown
//     alongside because that is the number the floor recognises.
//   • With more than two arms the intervals are NOT corrected for multiple
//     comparisons, and the panel says so where it matters. Four arms is four
//     chances to read "clear" out of noise.
//
// A wave appears here the moment it completes: the results endpoint is
// evicted by the wave-completion path, and this polls every 15s while the
// experiment is running. Off or finished, it fetches once — an experiment
// that is switched off should cost nothing.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import { FlaskConical, ChevronRight, Download, Upload, Trash2 } from "lucide-react";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";
import { useReadOnly } from "../../../access/ReadOnly";
import { useAccess } from "../../../access/AccessContext";
import { computeBays, runsForTier, LayoutModel, LayoutRun } from "../layout/layoutGeometry";

const POLL_MS = 15_000;

// Rows per upload request. A warehouse sheet is tens of thousands of rows and
// the BE's JSON body limit is 5mb, so the sheet is posted in chunks exactly
// as Settings > Locations does. The first chunk replaces, the rest append.
const CHUNK_SIZE = 2_000;

type PickTypeKey = "pick_and_sort" | "light" | "large";
// An arm code: 'standard', 'corridor', or an uploaded variant's code.
type Arm = string;
type Confidence = "insufficient" | "indicative" | "clear";

type TierKey = "low" | "high";

export interface WalkExperimentSettings {
    warehouse: string;
    picking_walk_pattern: Arm;
    picking_walk_experiment_enabled: boolean;
    picking_walk_experiment_pick_types: PickTypeKey[];
    // Optional so a settings payload from a BE that predates migration 178
    // still types; the card defaults it to ["low"].
    picking_walk_experiment_tiers?: TierKey[];
    picking_walk_experiment_variants: Arm[];
    picking_walk_experiment_started_at: string | null;
    picking_walk_experiment_ends_at: string | null;
}

interface ArmStats {
    arm: Arm;
    waves: number; pickers: number; units: number; picks: number;
    moves: number; same_loc_picks: number;
    active_seconds: number | null;
    median_s_per_move: number | null;
    median_s_per_same_loc_pick: number | null;
    median_s_per_pick: number | null;
    units_per_hour: number | null;
}

interface Comparison {
    arm: Arm;
    baseline_arm: Arm;
    delta_pct: number | null;
    faster: Arm | null;
    confidence: Confidence;
    ci95: { low_pct: number; high_pct: number } | null;
    min_moves_for_signal: number;
    baseline_median_s_per_move: number | null;
    arm_median_s_per_move: number | null;
    baseline_units_per_hour: number | null;
    arm_units_per_hour: number | null;
    units_per_hour_delta_pct: number | null;
}

interface Headline {
    pick_type: PickTypeKey;
    tier?: TierKey;
    baseline_arm: Arm;
    comparisons: Comparison[];
    best_arm: Arm;
    best_delta_pct: number | null;
    multiple_comparisons: boolean;
}

// Replay-derived prior for one arm — shown as "estimated" until measured
// waves exist. Warehouse-specific; absent means no prior, never zero.
interface ArmEstimate { delta_pct: number; ci_pct: number; basis?: string }

interface Results {
    arms: Arm[];
    headline: Headline[];
    byPickType: { pick_type: PickTypeKey; tier?: TierKey; baseline_arm: Arm; arms: ArmStats[] }[];
    recentWaves: {
        wave_id: number; pick_type: PickTypeKey; tier?: TierKey; walk_pattern: Arm;
        completed_at: string | null; picker_uid: number | null;
        units: number; moves: number;
        median_s_per_move: number | null; units_per_hour: number | null;
    }[];
    estimates?: Record<Arm, ArmEstimate>;
    // Everything else derives from the drawn plan live; a replay estimate is
    // a snapshot, so the endpoint says when it was measured and when the
    // layout last changed — the panel flags the mismatch.
    estimates_measured_at?: string;
    layout_updated_at?: string | null;
}

interface RouteMapData {
    arm: Arm;
    source: "all_locations" | "sample_wave";
    wave_id?: number;
    stops: { aisle: string; bay: string; x_m: number; y_m: number }[];
    // The walked line: stops plus the crossing waypoints between them, so the
    // route follows aisles and tunnels instead of cutting through racking.
    path: { x_m: number; y_m: number }[];
    // The dashed walk between the drop-zone base and the route's two ends —
    // real metres the picker pays, and for the dynamic loop, part of what
    // the route minimises.
    legs?: { x_m: number; y_m: number }[][];
    route_m: number;
    points: {
        kind: string; x_m: number; y_m: number;
        nearest_bay: string | null; distance_m: number | null;
    }[];
}

interface Variant {
    code: string;
    name: string;
    note: string | null;
    location_count: number;
    covered_now: number;
    warehouse_count: number;
    coverage_pct: number;
    created_at: string;
    updated_at: string;
}

interface PreviewStop {
    bays: { aisle: string | null; bay: string | null; zone: string | null }[];
    current: { location: string; aisle: string | null; bay: string | null; level: string | null; walk_order: number }[];
    corridor: { location: string; aisle: string | null; bay: string | null; level: string | null; walk_order: number }[];
}

interface Preview {
    stats: { lowLocations: number; reordered: number; singleSided: number; ambiguous: number; coverage_pct: number };
    samples: PreviewStop[];
    skipped: { reason: string; bays: { aisle: string | null; bay: string | null }[] }[];
}

interface ExportRow {
    location: string;
    zone: string | null; aisle: string | null; bay: string | null; level: string | null;
    high_low: string | null;
    walk_order: number | null;
    live_walk_order: number | null;
}

const BUILTIN: Arm[] = ["standard", "corridor", "dynamic", "aligned"];

// Large is enrollable but deliberately last: a Large wave is JIT, one order at
// a time, so the arm only ever reorders the lines WITHIN one order. It is a
// much weaker signal than Pick & Sort and the hint next to the chips says so.
const PICK_TYPES: { value: PickTypeKey; labelKey: string }[] = [
    { value: "pick_and_sort", labelKey: "picking_pick_and_sort_settings" },
    { value: "light",         labelKey: "picking_light_settings" },
    { value: "large",         labelKey: "picking_large_settings" },
];

// Sheet columns. `location` and the geometry columns are context for the
// person editing; `walk_order` is the only one read back on upload.
const SHEET_HEADER = [
    "location", "zone", "aisle", "bay", "level", "high_low",
    "walk_order", "live_walk_order",
] as const;

// Location barcodes are numeric-looking at some sites (5091909) and Excel
// will happily turn a long one into scientific notation, which then matches
// nothing on the way back in. Force these columns to Text.
const TEXT_COLUMNS = new Set<string>(["location", "zone", "aisle", "bay", "level", "high_low"]);

const fmt1 = (n: number | null | undefined) =>
    n === null || n === undefined || Number.isNaN(n) ? "—" : n.toFixed(1);
const fmt0 = (n: number | null | undefined) =>
    n === null || n === undefined || Number.isNaN(n) ? "—" : Math.round(n).toString();

// "YYYY-MM-DD" for <input type="date">, which is what the end-date control is.
const toDateInput = (iso: string | null) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");

// ExcelJS cell values arrive as strings, numbers, dates, formula results or
// rich text depending on how the sheet was produced. Same normaliser as
// Settings > Locations.
const cellToString = (cell: ExcelJS.Cell): string => {
    const v = cell?.value as unknown;
    if (v === null || v === undefined) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === "object") {
        if ("result" in v && (v as { result?: unknown }).result != null) {
            return String((v as { result: unknown }).result);
        }
        if ("text" in v && (v as { text?: unknown }).text != null) {
            return String((v as { text: unknown }).text);
        }
        if ("richText" in v) {
            const rt = (v as { richText?: { text: string }[] }).richText;
            return Array.isArray(rt) ? rt.map(r => r.text).join("") : "";
        }
    }
    return String(v);
};

const downloadXlsx = async (filename: string, rows: ExportRow[]) => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Walk order");
    ws.addRow([...SHEET_HEADER]);
    for (const r of rows) {
        ws.addRow([
            r.location, r.zone ?? "", r.aisle ?? "", r.bay ?? "", r.level ?? "",
            r.high_low ?? "",
            r.walk_order ?? "", r.live_walk_order ?? "",
        ]);
    }
    SHEET_HEADER.forEach((name, i) => {
        if (TEXT_COLUMNS.has(name)) ws.getColumn(i + 1).numFmt = "@";
    });
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const readSheet = async (file: File): Promise<{ location: string; walk_order: string }[]> => {
    const buffer = await file.arrayBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buffer);
    const ws = wb.worksheets[0];
    if (!ws || ws.rowCount < 2) return [];
    const headerRow = ws.getRow(1);
    const header: string[] = [];
    for (let c = 1; c <= headerRow.cellCount; c++) {
        header[c - 1] = cellToString(headerRow.getCell(c)).trim().toLowerCase();
    }
    const locIdx = header.indexOf("location");
    const walkIdx = header.indexOf("walk_order");
    if (locIdx === -1 || walkIdx === -1) throw new Error("header");
    const out: { location: string; walk_order: string }[] = [];
    for (let r = 2; r <= ws.rowCount; r++) {
        const row = ws.getRow(r);
        const location = cellToString(row.getCell(locIdx + 1)).trim();
        const walk = cellToString(row.getCell(walkIdx + 1)).trim();
        if (!location && !walk) continue;
        out.push({ location, walk_order: walk });
    }
    return out;
};

// ─── Walk orders: download, upload, enrol ───────────────────────────────────

const WalkOrders = ({
    warehouse, variants, enrolled, adopted, busy,
    onReload, onEnrolChange, onPreview,
}: {
    warehouse: string;
    variants: Variant[];
    enrolled: Arm[];
    adopted: Arm;
    busy: boolean;
    onReload: () => void;
    onEnrolChange: (next: Arm[]) => void;
    onPreview: () => void;
}) => {
    const { t } = useTranslation();
    // Upload / delete a variant is its own key (admin.picking.walk_variants, 2026-09-30); the page's
    // read-only state still applies on top.
    const pageReadOnly = useReadOnly();
    const { authoritative, canWrite } = useAccess();
    const readOnly = pageReadOnly || (authoritative && !canWrite("admin.picking.walk_variants"));
    const [code, setCode] = useState("");
    const [name, setName] = useState("");
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
    const fileRef = useRef<HTMLInputElement | null>(null);

    const download = async (variant: Arm | null) => {
        try {
            const qs = new URLSearchParams({ warehouse });
            if (variant) qs.set("variant", variant);
            const res = await apiFetch(`/admin/picking/walk-variants/export?${qs.toString()}`, {});
            if (!res.ok) throw new Error("export");
            const { rows } = await res.json() as { rows: ExportRow[] };
            const stamp = new Date().toISOString().slice(0, 10);
            await downloadXlsx(
                `walk-order-${warehouse}${variant ? `-${variant}` : ""}-${stamp}.xlsx`, rows);
        } catch {
            toast.error(t("walk_variant_download_failed"), { toastId: `wv-dl-${warehouse}` });
        }
    };

    const upload = async (file: File) => {
        const cleanCode = code.trim();
        if (!cleanCode) {
            toast.error(t("walk_variant_need_code"), { toastId: "wv-code" });
            return;
        }
        let rows: { location: string; walk_order: string }[];
        try {
            rows = await readSheet(file);
        } catch {
            toast.error(t("walk_variant_bad_header"), { toastId: "wv-hdr" });
            return;
        }
        if (rows.length === 0) {
            toast.error(t("walk_variant_empty"), { toastId: "wv-empty" });
            return;
        }

        setProgress({ done: 0, total: rows.length });
        try {
            let last: { accepted: number; total: number; coverage_pct: number;
                unknown: string[]; invalid: string[]; duplicate: string[] } | null = null;
            let unknown = 0, invalid = 0;
            for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
                const chunk = rows.slice(i, i + CHUNK_SIZE);
                const isFirst = i === 0;
                const isLast = i + CHUNK_SIZE >= rows.length;
                const res = await apiFetch("/admin/picking/walk-variants", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        warehouse,
                        code: cleanCode,
                        name: name.trim() || cleanCode.toUpperCase(),
                        rows: chunk,
                        mode: isFirst ? "replace" : "append",
                        final: isLast,
                    }),
                });
                const body = await res.json().catch(() => null);
                if (!res.ok) {
                    toast.error(body?.message ?? t("server_error"), { toastId: "wv-up" });
                    return;
                }
                last = body;
                unknown += body?.unknown?.length ?? 0;
                invalid += body?.invalid?.length ?? 0;
                setProgress({ done: Math.min(i + chunk.length, rows.length), total: rows.length });
            }
            // Coverage and the reject counts are the whole point of the
            // confirmation: a sheet that only half-matched still uploads, and
            // silently running a half-ordering as an arm is the one failure
            // mode that would look like a result rather than a mistake.
            toast.success(
                t("walk_variant_uploaded")
                    .replace("{code}", cleanCode.toUpperCase())
                    .replace("{n}", String(last?.total ?? 0))
                    .replace("{pct}", (last?.coverage_pct ?? 0).toFixed(0)),
                { toastId: "wv-ok", autoClose: 8000 },
            );
            if (unknown > 0 || invalid > 0) {
                toast.warn(
                    t("walk_variant_rejects")
                        .replace("{unknown}", String(unknown))
                        .replace("{invalid}", String(invalid)),
                    { toastId: "wv-rej", autoClose: 10000 },
                );
            }
            setCode("");
            setName("");
            onReload();
        } catch {
            toast.error(t("server_error"), { toastId: "wv-up" });
        } finally {
            setProgress(null);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const remove = async (variant: Variant) => {
        const msg = enrolled.includes(variant.code)
            ? t("walk_variant_delete_enrolled").replace("{code}", variant.code.toUpperCase())
            : t("walk_variant_delete_confirm").replace("{code}", variant.code.toUpperCase());
        if (!window.confirm(msg)) return;
        try {
            const res = await apiFetch(
                `/admin/picking/walk-variants/${encodeURIComponent(variant.code)}`
                + `?warehouse=${encodeURIComponent(warehouse)}`,
                { method: "DELETE" });
            if (!res.ok) throw new Error("delete");
            if (enrolled.includes(variant.code)) {
                onEnrolChange(enrolled.filter(a => a !== variant.code));
            }
            onReload();
        } catch {
            toast.error(t("server_error"), { toastId: `wv-del-${variant.code}` });
        }
    };

    const toggleArm = (arm: Arm) => {
        const next = enrolled.includes(arm)
            ? enrolled.filter(a => a !== arm)
            : [...enrolled, arm];
        // One arm is not a comparison. Emptying the list entirely is allowed
        // (it parks the experiment); landing on exactly one is not, because
        // the wave builder would silently enrol nobody.
        if (next.length === 1) {
            toast.error(t("walk_variant_need_two_arms"), { toastId: "wv-arms" });
            return;
        }
        onEnrolChange(next);
    };

    const armRow = (arm: Arm, label: string, detail: string, v: Variant | null) => (
        <div className="admin-settings-row walk-exp-variant-row" key={arm}>
            <div className="admin-settings-warehouse">
                <span className={`walk-exp-arm-pill walk-exp-arm-pill--${arm}`}>{arm.toUpperCase()}</span>
                {" "}{label}
                {adopted === arm && (
                    <span className="walk-exp-variant-live">{t("walk_variant_adopted")}</span>
                )}
            </div>
            <div className="walk-exp-variant-detail">{detail}</div>
            <div>
                <button
                    type="button"
                    aria-pressed={enrolled.includes(arm)}
                    disabled={busy || readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : undefined}
                    className={`walk-exp-chip${enrolled.includes(arm) ? " walk-exp-chip--on" : ""}`}
                    onClick={() => toggleArm(arm)}
                >
                    {t("walk_variant_in_test")}
                </button>
            </div>
            <div className="walk-exp-variant-actions">
                {arm === "corridor" && (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={onPreview}>
                        {t("walk_experiment_preview")}
                    </button>
                )}
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    title={t("walk_variant_download")}
                    onClick={() => void download(arm === "standard" ? null : arm)}
                >
                    <Download size={14} strokeWidth={2} />
                </button>
                {v && (
                    <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        title={readOnly ? t("read_only_mode") : t("walk_variant_delete")}
                        disabled={readOnly}
                        aria-disabled={readOnly}
                        onClick={() => void remove(v)}
                    >
                        <Trash2 size={14} strokeWidth={2} />
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <div className="walk-exp-variants">
            <h5 className="walk-exp-subhead">{t("walk_variant_section")}</h5>
            <p className="admin-settings-section-hint">{t("walk_variant_hint")}</p>

            <div className="admin-settings-table admin-settings-table--4col walk-exp-table">
                <div className="admin-settings-row admin-settings-row--head">
                    <div>{t("walk_variant_col_arm")}</div>
                    <div>{t("walk_variant_col_coverage")}</div>
                    <div>{t("walk_variant_col_in_test")}</div>
                    <div>{t("walk_variant_col_actions")}</div>
                </div>
                {armRow("standard", t("walk_pattern_standard"), t("walk_variant_builtin_standard"), null)}
                {armRow("corridor", t("walk_pattern_corridor"), t("walk_variant_builtin_corridor"), null)}
                {armRow("dynamic", t("walk_pattern_dynamic"), t("walk_variant_builtin_dynamic"), null)}
                {armRow("aligned", t("walk_pattern_aligned"), t("walk_variant_builtin_aligned"), null)}
                {variants.map(v => armRow(
                    v.code,
                    v.name,
                    t("walk_variant_coverage")
                        .replace("{n}", String(v.covered_now))
                        .replace("{total}", String(v.warehouse_count))
                        .replace("{pct}", v.coverage_pct.toFixed(0)),
                    v,
                ))}
            </div>

            <div className="walk-exp-upload">
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => void download(null)}
                >
                    <Download size={14} strokeWidth={2} /> {t("walk_variant_download_current")}
                </button>
                <input
                    className="walk-exp-code-input"
                    type="text"
                    maxLength={24}
                    placeholder={t("walk_variant_code_placeholder")}
                    value={code}
                    onChange={e => setCode(e.target.value)}
                />
                <input
                    className="walk-exp-name-input"
                    type="text"
                    maxLength={120}
                    placeholder={t("walk_variant_name_placeholder")}
                    value={name}
                    onChange={e => setName(e.target.value)}
                />
                <label className={`btn btn-primary btn-sm${progress || readOnly ? " disabled" : ""}`} title={readOnly ? t("read_only_mode") : undefined}>
                    <Upload size={14} strokeWidth={2} />
                    {progress
                        ? t("walk_variant_uploading")
                            .replace("{done}", String(progress.done))
                            .replace("{total}", String(progress.total))
                        : t("walk_variant_upload")}
                    <input
                        ref={fileRef}
                        type="file"
                        hidden
                        disabled={!!progress || readOnly}
                        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) void upload(f);
                        }}
                    />
                </label>
            </div>
            <p className="walk-exp-control-hint">{t("walk_variant_upload_hint")}</p>
        </div>
    );
};

// ─── One warehouse ──────────────────────────────────────────────────────────

// A compact collapsible sub-section. Everything inside the card folds so a
// warehouse row reads as a short stack of headed lines, not a wall.
//
// `lazy` mounts the children only WHILE the section is open — for content
// whose effects fetch (the route map). A closed <details> still RENDERS its
// children, so without this a collapsed card would fire network requests,
// which the "fetches nothing until opened" contract (and its test) forbids.
// Unmounting on close (rather than keeping the first mount alive) also means
// every re-open fetches fresh — so a drop zone just moved in the Warehouse
// layout editor shows up on the next expand instead of being cached forever.
const Section = ({ title, meta, defaultOpen = false, lazy = false, children }: {
    title: string;
    meta?: string;
    defaultOpen?: boolean;
    lazy?: boolean;
    children: ReactNode;
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <details
            className="walk-exp-sec"
            open={defaultOpen}
            onToggle={e => setIsOpen((e.currentTarget as HTMLDetailsElement).open)}
        >
            <summary className="walk-exp-sec-head">
                <ChevronRight className="walk-exp-caret" size={14} strokeWidth={2} />
                <span>{title}</span>
                {meta && <span className="walk-exp-sec-meta">{meta}</span>}
            </summary>
            <div className="walk-exp-sec-body">{(!lazy || isOpen) ? children : null}</div>
        </details>
    );
};

// ── 2D route map ─────────────────────────────────────────────────────────
//
// The warehouse plan (bays from the drawn layout, exactly as the Warehouse
// layout editor places them) with one arm's walk drawn over it. Static arms
// draw the full-warehouse order; 'dynamic' draws the most recent completed
// wave through the real router, labelled as a sample. Direction is shown by
// arrowheads along the line plus start/end markers.
const RouteMap = ({ warehouse, arms, labelOf }: {
    warehouse: string;
    arms: Arm[];
    labelOf: (a: Arm) => string;
}) => {
    const { t } = useTranslation();
    const [arm, setArm] = useState<Arm>("standard");
    const [view, setView] = useState<"wave" | "full">("wave");
    const [tier, setTier] = useState<TierKey>("low");
    // Off by default: the route line already reads start-to-end, and the
    // chevrons are visual weight the plan doesn't carry.
    const [showArrows, setShowArrows] = useState(false);
    const [model, setModel] = useState<LayoutModel | null>(null);
    const [modelMissing, setModelMissing] = useState(false);
    const [route, setRoute] = useState<RouteMapData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const aliveRef = useRef(true);

    useEffect(() => {
        aliveRef.current = true;
        void (async () => {
            try {
                const res = await apiFetch(
                    `/warehouse-layout?warehouse=${encodeURIComponent(warehouse)}`, {});
                if (!res.ok || !aliveRef.current) return;
                const body = await res.json();
                if (body.exists && body.model) setModel(body.model);
                else setModelMissing(true);
            } catch { setModelMissing(true); }
        })();
        return () => { aliveRef.current = false; };
    }, [warehouse]);

    useEffect(() => {
        setRoute(null);
        setError(null);
        void (async () => {
            try {
                const res = await apiFetch(
                    "/admin/picking/experiment/walk-pattern/route-map"
                    + `?warehouse=${encodeURIComponent(warehouse)}&arm=${encodeURIComponent(arm)}&view=${view}&tier=${tier}`, {});
                if (!aliveRef.current) return;
                if (!res.ok) {
                    const body = await res.json().catch(() => null);
                    setError(body?.message ?? t("server_error"));
                    return;
                }
                setRoute(await res.json());
            } catch { if (aliveRef.current) setError(t("server_error")); }
        })();
    }, [warehouse, arm, view, tier, t]);

    const bays = useMemo(
        () => (model ? computeBays(model, tier).filter(b => !b.unplaced) : []),
        [model, tier],
    );

    // The 2D plan editor draws Y negated (origin at the bottom), so the map
    // flips the same way — otherwise the two pictures are mirror images of
    // each other and nothing lines up by eye.
    const Y = (y: number) => -y;

    // Rack depth per aisle — the racking is drawn at its real depth, exactly
    // as the Warehouse layout plan draws it.
    const depthOf = useMemo(() => {
        const m = new Map<string, number>();
        for (const a of model?.aisles ?? []) m.set(a.aisle, a.rack_depth_m);
        return m;
    }, [model]);

    const faceXOf = (aisle: string): number | null => {
        const a = model?.aisles.find(x => x.aisle === aisle);
        const c = a && model?.corridors.find(x => x.id === a.corridor_id);
        if (!a || !c) return null;
        return a.side === "b" ? c.x_m + c.width_m / 2 : c.x_m - c.width_m / 2;
    };

    // Plan extent — same formula as the layout editor's, racking depth and
    // route endpoints included, so both pictures frame the shed identically.
    const box = useMemo(() => {
        if (bays.length === 0) return null;
        let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
        for (const b of bays) {
            const d = depthOf.get(b.aisle) ?? 0.6;
            const bx0 = b.side === "b" ? (b.x_m as number) : (b.x_m as number) - d;
            const bx1 = b.side === "b" ? (b.x_m as number) + d : (b.x_m as number);
            x0 = Math.min(x0, bx0); x1 = Math.max(x1, bx1);
            y0 = Math.min(y0, -((b.along_m as number) + (b.width_m as number) / 2));
            y1 = Math.max(y1, -((b.along_m as number) - (b.width_m as number) / 2));
        }
        for (const p of route?.points ?? []) {
            x0 = Math.min(x0, p.x_m - 1); x1 = Math.max(x1, p.x_m + 1);
            y0 = Math.min(y0, -p.y_m - 1); y1 = Math.max(y1, -p.y_m + 1);
        }
        const pad = 2;
        return { x0: x0 - pad, y0: y0 - pad, w: x1 - x0 + 2 * pad, h: y1 - y0 + 2 * pad };
    }, [bays, depthOf, route]);

    const labelSize = box ? Math.max(box.w, box.h) * 0.022 : 1;

    const stops = route?.stops ?? [];
    // Draw the walked line (aisles + crossings), not stop-to-stop chords.
    const line = (route?.path?.length ?? 0) > 1 ? route!.path : stops;
    const poly = line.map(s => `${s.x_m},${Y(s.y_m)}`).join(" ");
    // Direction arrows: spaced by walked DISTANCE along the line, rotated
    // with the segment they sit on (in flipped screen coordinates).
    const arrows = useMemo(() => {
        const out: { x: number; y: number; deg: number }[] = [];
        let total = 0;
        for (let i = 1; i < line.length; i++) {
            total += Math.hypot(line[i].x_m - line[i - 1].x_m, line[i].y_m - line[i - 1].y_m);
        }
        if (total === 0) return out;
        const spacing = Math.max(8, total / 26);
        let next = spacing;
        let walked = 0;
        for (let i = 1; i < line.length; i++) {
            const a = line[i - 1];
            const b = line[i];
            const seg = Math.hypot(b.x_m - a.x_m, b.y_m - a.y_m);
            while (seg > 0.2 && walked + seg >= next) {
                const f = (next - walked) / seg;
                out.push({
                    x: a.x_m + (b.x_m - a.x_m) * f,
                    y: -(a.y_m + (b.y_m - a.y_m) * f),
                    deg: (Math.atan2(-(b.y_m - a.y_m), b.x_m - a.x_m) * 180) / Math.PI,
                });
                next += spacing;
            }
            walked += seg;
        }
        return out;
    }, [line]);

    if (modelMissing) {
        return <p className="admin-settings-empty">{t("walk_exp_map_no_layout")}</p>;
    }
    return (
        <div className="walk-exp-map">
            <div className="walk-exp-map-controls">
                <span className="walk-exp-control-label">{t("walk_exp_map_arm")}</span>
                <select
                    className="walk-exp-select walk-exp-select--sm"
                    value={arm}
                    onChange={e => setArm(e.target.value)}
                >
                    {arms.map(a => <option key={a} value={a}>{labelOf(a)}</option>)}
                </select>
                <select
                    className="walk-exp-select walk-exp-select--sm"
                    value={view}
                    onChange={e => setView(e.target.value as "wave" | "full")}
                >
                    <option value="wave">{t("walk_exp_map_view_wave")}</option>
                    <option value="full">{t("walk_exp_map_view_full")}</option>
                </select>
                <div className="wlayout-tier-toggle">
                    {(["low", "high"] as const).map(k => (
                        <button
                            key={k}
                            type="button"
                            aria-pressed={tier === k}
                            className={`btn btn-sm dispatch-modal-btn${tier === k ? " btn-primary" : " btn-secondary"}`}
                            onClick={() => setTier(k)}
                        >
                            {t(`wlayout_tier_${k}`)}
                        </button>
                    ))}
                </div>
                <button
                    type="button"
                    aria-pressed={showArrows}
                    className={`btn btn-sm dispatch-modal-btn${showArrows ? " btn-primary" : " btn-secondary"}`}
                    onClick={() => setShowArrows(s => !s)}
                >
                    {t("walk_exp_map_arrows")}
                </button>
                {route && (
                    <span className="walk-exp-map-meta">
                        {route.source === "sample_wave"
                            ? t("walk_exp_map_sample")
                                .replace("{id}", String(route.wave_id ?? ""))
                                .replace("{m}", String(route.route_m))
                            : t("walk_exp_map_full").replace("{m}", String(route.route_m))}
                    </span>
                )}
            </div>
            {error && <p className="admin-settings-empty">{error}</p>}
            {!error && (!box || !route) && <div className="admin-settings-empty">{t("loading")}</div>}
            {!error && box && route && (
                <>
                    <svg
                        className="wlayout-canvas walk-exp-map-canvas"
                        viewBox={`${box.x0} ${box.y0} ${box.w} ${box.h}`}
                        preserveAspectRatio="xMidYMid meet"
                        role="img"
                        aria-label={t("walk_exp_map_title")}
                    >
                        {/* The RACKING and BAYS, drawn exactly as the Warehouse
                            layout plan draws them — same shelves, same depths,
                            same classes — so the two pictures are the same
                            drawing with a route on top. */}
                        {model!.aisles.map((a) => {
                            const d = depthOf.get(a.aisle) ?? 0.6;
                            const face = faceXOf(a.aisle);
                            if (face == null) return null;
                            let cursor = a.origin_offset_m;
                            return (
                                <g key={`rack-${a.aisle}`} className="wlayout-rack">
                                    {runsForTier(a, tier).map((r: LayoutRun) => {
                                        const start = cursor;
                                        cursor += r.shelf_width_m + r.upright_width_m;
                                        const h = Math.max(r.shelf_width_m, 0.02);
                                        return (
                                            <rect
                                                key={r.seq}
                                                className={`wlayout-shelf${
                                                    r.locations_per_shelf === 0 ? " wlayout-shelf--gap" : ""}`}
                                                x={a.side === "b" ? face : face - d}
                                                y={Y(start) - h}
                                                width={d}
                                                height={h}
                                            />
                                        );
                                    })}
                                </g>
                            );
                        })}
                        {bays.map(b => {
                            const d = depthOf.get(b.aisle) ?? 0.6;
                            const h = Math.max((b.width_m as number) - 0.04, 0.02);
                            return (
                                <rect
                                    key={`${b.aisle}-${b.bay}`}
                                    className={`wlayout-bay wlayout-bay--${b.side}`}
                                    x={b.side === "b" ? (b.x_m as number) : (b.x_m as number) - d}
                                    y={Y((b.along_m as number) - (b.width_m as number) / 2) - h}
                                    width={d}
                                    height={h}
                                />
                            );
                        })}
                        {/* Every visited bay, dotted at its pick face — the
                            proof that one corridor line serves the aisles on
                            BOTH of its sides. A gap with no dots is a rack
                            back, not a missed aisle. */}
                        {stops.map((s, i) => (
                            <circle
                                key={`stop-${s.aisle}-${s.bay}-${i}`}
                                className="walk-exp-map-stopdot"
                                cx={s.x_m}
                                cy={Y(s.y_m)}
                                r={0.16}
                            />
                        ))}
                        {(route.legs ?? []).map((leg, i) => (
                            <polyline
                                key={`leg-${i}`}
                                className="walk-exp-map-leg"
                                points={leg.map(p => `${p.x_m},${Y(p.y_m)}`).join(" ")}
                            />
                        ))}
                        {poly && <polyline className="walk-exp-map-route" points={poly} />}
                        {showArrows && arrows.map((a, i) => (
                            <path
                                key={i}
                                className="walk-exp-map-arrow"
                                d="M -0.6 -0.45 L 0.6 0 L -0.6 0.45 Z"
                                transform={`translate(${a.x} ${a.y}) rotate(${a.deg})`}
                            />
                        ))}
                        {stops.length > 0 && (
                            <>
                                <circle className="walk-exp-map-start" cx={stops[0].x_m} cy={Y(stops[0].y_m)} r={0.6} />
                                <circle className="walk-exp-map-end" cx={stops[stops.length - 1].x_m} cy={Y(stops[stops.length - 1].y_m)} r={0.6} />
                            </>
                        )}
                        {/* Aisle letters at both ends, sized like the plan's. */}
                        {model!.aisles.map((a) => {
                            const d = depthOf.get(a.aisle) ?? 0.6;
                            const face = faceXOf(a.aisle);
                            if (face == null) return null;
                            const x = a.side === "b" ? face + d / 2 : face - d / 2;
                            return (
                                <g key={`lbl-${a.aisle}`}>
                                    <text className="wlayout-aisle-label" fontSize={labelSize}
                                          x={x} y={box.y0 + labelSize * 1.4} textAnchor="middle">
                                        {a.aisle}
                                    </text>
                                    <text className="wlayout-aisle-label" fontSize={labelSize}
                                          x={x} y={box.y0 + box.h - labelSize * 0.5} textAnchor="middle">
                                        {a.aisle}
                                    </text>
                                </g>
                            );
                        })}
                        {/* Route endpoints, marked as the plan marks them. */}
                        {(route.points ?? []).map(p => (
                            <g key={p.kind} className="wlayout-point">
                                <circle cx={p.x_m} cy={Y(p.y_m)} r={0.8} />
                                <text x={p.x_m} y={Y(p.y_m) - 1.2} textAnchor="middle" fontSize={labelSize * 0.8}>
                                    {t(`wlayout_point_${p.kind}`)}
                                </text>
                            </g>
                        ))}
                    </svg>
                    <div className="walk-exp-map-legend">
                        <span><i className="walk-exp-map-dot walk-exp-map-dot--start" /> {t("walk_exp_map_start")}</span>
                        <span><i className="walk-exp-map-dot walk-exp-map-dot--end" /> {t("walk_exp_map_end")}</span>
                        {(route.points ?? []).filter(p => p.distance_m !== null).map(p => (
                            <span key={p.kind}>
                                {t(`wlayout_point_${p.kind}`)}
                                {" — "}
                                {t("walk_exp_map_point_distance")
                                    .replace("{m}", String(p.distance_m))
                                    .replace("{bay}", p.nearest_bay ?? "?")}
                            </span>
                        ))}
                    </div>
                    {route.source === "all_locations" && (
                        <p className="walk-exp-control-hint">{t("walk_exp_map_full_hint")}</p>
                    )}
                </>
            )}
        </div>
    );
};

const Experiment = ({ row, onSaved }: {
    row: WalkExperimentSettings;
    onSaved: (patch: Partial<WalkExperimentSettings>) => void;
}) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const warehouse = row.warehouse;

    const [results, setResults] = useState<Results | null>(null);
    const [variants, setVariants] = useState<Variant[]>([]);
    const [preview, setPreview] = useState<Preview | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [pending, setPending] = useState<string | null>(null);
    const [loaded, setLoaded] = useState(false);
    // Collapsed by default. With ~13 warehouses the expanded list is
    // unscannable, and every card used to fetch on mount and hold its own 15s
    // poll — so opening the section cost 13 requests and 13 standing timers
    // for the one warehouse anybody was looking at.
    const [open, setOpen] = useState(false);
    // Guards the poll against setting state after unmount / warehouse switch.
    const aliveRef = useRef(true);

    const loadResults = useCallback(async () => {
        try {
            const res = await apiFetch(
                `/admin/picking/experiment/walk-pattern?warehouse=${encodeURIComponent(warehouse)}`, {});
            if (!res.ok || !aliveRef.current) return;
            setResults(await res.json());
        } catch { /* keep the last good numbers on a blip */ } finally {
            if (aliveRef.current) setLoaded(true);
        }
    }, [warehouse]);

    const loadVariants = useCallback(async () => {
        try {
            const res = await apiFetch(
                `/admin/picking/walk-variants?warehouse=${encodeURIComponent(warehouse)}`, {});
            if (!res.ok || !aliveRef.current) return;
            const body = await res.json();
            setVariants(body.variants ?? []);
        } catch { /* the variants table just stays as it was */ }
    }, [warehouse]);

    // Fetch nothing until the card is opened, then poll only while the
    // experiment is actually running — that is the only state in which new
    // waves can land. Off or finished, the results are fetched once so past
    // runs stay readable without a standing poll.
    //
    // The collapsed summary deliberately needs no fetched data: it renders
    // from `row`, which the parent already has. The one visible consequence is
    // that a warehouse whose adopted arm is an UPLOADED variant shows the bare
    // code until first expand, because the pretty name lives in `variants`.
    const running = row.picking_walk_experiment_enabled;
    useEffect(() => {
        if (!open) return;
        aliveRef.current = true;
        void loadResults();
        void loadVariants();
        const id = running
            ? window.setInterval(() => { void loadResults(); }, POLL_MS)
            : null;
        return () => {
            aliveRef.current = false;
            if (id !== null) window.clearInterval(id);
        };
    }, [loadResults, loadVariants, running, open]);

    const loadPreview = useCallback(async () => {
        try {
            const res = await apiFetch(
                `/admin/picking/experiment/walk-pattern/preview?warehouse=${encodeURIComponent(warehouse)}`, {});
            if (!res.ok || !aliveRef.current) return;
            setPreview(await res.json());
        } catch { /* the disclosure just stays empty */ }
    }, [warehouse]);

    const patch = async (body: Partial<WalkExperimentSettings>, key: string) => {
        setPending(key);
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(warehouse)}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const msg = await res.json().catch(() => null);
                throw new Error(msg?.message ?? "save");
            }
            onSaved(body);
        } catch (err) {
            toast.error(
                err instanceof Error && err.message !== "save" ? err.message : t("server_error"),
                { toastId: `walk-exp-err-${warehouse}` },
            );
        } finally {
            setPending(null);
        }
    };

    const enrolled = row.picking_walk_experiment_variants ?? [];

    const toggleEnabled = (next: boolean) => {
        if (readOnly) return;
        if (next && enrolled.length < 2) {
            toast.error(t("walk_variant_need_two_arms"), { toastId: "wv-arms" });
            return;
        }
        // Starting the experiment stamps started_at, which anchors the
        // rotation counter — without it a restart would inherit the previous
        // run's tally and open on the wrong arm.
        void patch(next
            ? {
                picking_walk_experiment_enabled: true,
                picking_walk_experiment_started_at: new Date().toISOString(),
            }
            : { picking_walk_experiment_enabled: false },
            "enabled");
    };

    const togglePickType = (value: PickTypeKey, on: boolean) => {
        if (readOnly) return;
        const next = on
            ? [...row.picking_walk_experiment_pick_types, value]
            : row.picking_walk_experiment_pick_types.filter(p => p !== value);
        if (next.length === 0) {
            toast.error(t("walk_experiment_need_one_pick_type"), { toastId: "walk-exp-types" });
            return;
        }
        void patch({ picking_walk_experiment_pick_types: next }, `type-${value}`);
    };

    const tiers = row.picking_walk_experiment_tiers ?? ["low"];
    const toggleTier = (value: TierKey, on: boolean) => {
        if (readOnly) return;
        const next = on ? [...tiers, value] : tiers.filter(x => x !== value);
        if (next.length === 0) {
            toast.error(t("walk_experiment_need_one_tier"), { toastId: "walk-exp-tiers" });
            return;
        }
        void patch({ picking_walk_experiment_tiers: next }, `tier-${value}`);
    };

    // Every arm that could be adopted: the built-ins, whatever has been
    // uploaded, and — so a warehouse can never be stranded on an arm the list
    // has forgotten — whatever is currently adopted.
    //
    // Every arm is named `CODE · Name`, built-ins included. The code is what
    // is stamped on picking_waves.walk_pattern and what an uploaded sheet is
    // keyed on, so a results row that says only "Alternating" cannot be
    // matched back to anything. Uploaded variants already read this way; this
    // just stops the built-ins being the exception.
    const armLabels = useMemo(() => {
        const m = new Map<Arm, string>([
            ["standard", `STANDARD · ${t("walk_pattern_standard")}`],
            ["corridor", `CORRIDOR · ${t("walk_pattern_corridor")}`],
            ["dynamic", `DYNAMIC · ${t("walk_pattern_dynamic")}`],
            ["aligned", `ALIGNED · ${t("walk_pattern_aligned")}`],
        ]);
        for (const v of variants) m.set(v.code, `${v.code.toUpperCase()} · ${v.name}`);
        return m;
    }, [variants, t]);
    const labelOf = (arm: Arm) => armLabels.get(arm) ?? arm.toUpperCase();

    const adoptable = useMemo(() => {
        const codes = [...BUILTIN, ...variants.map(v => v.code)];
        if (!codes.includes(row.picking_walk_pattern)) codes.push(row.picking_walk_pattern);
        return codes;
    }, [variants, row.picking_walk_pattern]);

    const adopt = (arm: Arm) => {
        if (readOnly) return;
        const h = results?.headline?.[0];
        const cmp = h?.comparisons.find(c => c.arm === arm);
        const summary = cmp?.delta_pct != null
            ? t("walk_experiment_adopt_confirm_measured")
                .replace("{pct}", Math.abs(cmp.delta_pct).toFixed(1))
                .replace("{winner}", labelOf(cmp.faster ?? arm))
                .replace("{confidence}", t(`walk_experiment_confidence_${cmp.confidence}`))
            : t("walk_experiment_adopt_confirm_nodata");
        if (!window.confirm(
            `${summary}\n\n${t("walk_experiment_adopt_confirm").replace("{pattern}", labelOf(arm))}`,
        )) return;
        void patch({
            picking_walk_pattern: arm,
            picking_walk_experiment_enabled: false,
        }, "adopt");
    };

    const endsAt = row.picking_walk_experiment_ends_at;
    const ended = !!endsAt && new Date(endsAt).getTime() <= Date.now();
    const hasComparison = (results?.headline ?? []).some(h => h.comparisons.length > 0);

    return (
        <details
            className="walk-exp"
            open={open}
            onToggle={e => setOpen((e.currentTarget as HTMLDetailsElement).open)}
        >
            {/* The enable switch lives in the summary so a collapsed card can
                still be turned on and off. Clicking it must NOT also expand
                the card — and React's stopPropagation cannot stop that,
                because <summary>'s activation is a native default action, not
                a bubbling handler. preventDefault does stop it, but it also
                cancels the checkbox's own activation, so the switch is driven
                from `running` here rather than from the DOM (hence readOnly
                on the input). Keyboard activation lands here too: space on
                the focused checkbox dispatches a click. */}
            <summary
                className="walk-exp-head"
                onClick={(e) => {
                    if (!(e.target as HTMLElement).closest(".walk-exp-enable")) return;
                    e.preventDefault();
                    if (pending !== "enabled") toggleEnabled(!running);
                }}
            >
                <ChevronRight className="walk-exp-caret" size={16} strokeWidth={2} />
                <div className="walk-exp-warehouse">{warehouse}</div>
                <div className="walk-exp-status">
                    {running && !ended && (
                        <span className="walk-exp-badge walk-exp-badge--live">
                            {endsAt
                                ? t("walk_experiment_running_until").replace("{date}", toDateInput(endsAt))
                                : t("walk_experiment_running")}
                            {" · "}
                            {t("walk_experiment_arms_running")
                                .replace("{n}", String(enrolled.length))}
                        </span>
                    )}
                    {running && ended && (
                        <span className="walk-exp-badge">{t("walk_experiment_finished")}</span>
                    )}
                    {!running && (
                        <span
                            className="walk-exp-badge walk-exp-badge--off"
                            // The collapsed row shows the arm CODE only — the
                            // pretty name is a full sentence and five closed
                            // warehouses of it is a wall. The full label lives
                            // one click away inside the card; the title
                            // carries it for a hover.
                            title={labelOf(row.picking_walk_pattern)}
                        >
                            {t("walk_experiment_off")
                                .replace("{pattern}", row.picking_walk_pattern.toUpperCase())}
                        </span>
                    )}
                </div>
                <label className="admin-settings-toggle walk-exp-enable">
                    <input
                        type="checkbox"
                        checked={running}
                        disabled={pending === "enabled" || readOnly}
                        readOnly
                    />
                    <span className="admin-settings-slider" />
                </label>
            </summary>

            {/* ── The arms ─────────────────────────────────────────────── */}
            <Section
                title={t("walk_exp_sec_arms")}
                meta={t("walk_exp_sec_arms_meta").replace("{n}", String(enrolled.length))}
            >
                <WalkOrders
                    warehouse={warehouse}
                    variants={variants}
                    enrolled={enrolled}
                    adopted={row.picking_walk_pattern}
                    busy={pending === "arms"}
                    onReload={() => void loadVariants()}
                    onEnrolChange={next => void patch(
                        { picking_walk_experiment_variants: next }, "arms")}
                    onPreview={() => {
                        setPreviewOpen(true);
                        if (!preview) void loadPreview();
                    }}
                />
            </Section>

            {/* ── Controls ─────────────────────────────────────────────── */}
            <Section
                title={t("walk_exp_sec_controls")}
                meta={row.picking_walk_experiment_pick_types.map(p =>
                    t(PICK_TYPES.find(x => x.value === p)?.labelKey ?? p)).join(" · ")}
            >
            <div className="walk-exp-controls">
                <div className="walk-exp-control">
                    <span className="walk-exp-control-label">{t("walk_experiment_pick_types")}</span>
                    <div className="walk-exp-chips">
                        {PICK_TYPES.map(pt => {
                            const on = row.picking_walk_experiment_pick_types.includes(pt.value);
                            return (
                                <button
                                    key={pt.value}
                                    type="button"
                                    aria-pressed={on}
                                    disabled={pending === `type-${pt.value}` || readOnly}
                                    className={`walk-exp-chip${on ? " walk-exp-chip--on" : ""}`}
                                    onClick={() => togglePickType(pt.value, !on)}
                                >
                                    {t(pt.labelKey)}
                                </button>
                            );
                        })}
                    </div>
                    {row.picking_walk_experiment_pick_types.includes("large") && (
                        <span className="walk-exp-control-hint">
                            {t("walk_experiment_large_hint")}
                        </span>
                    )}
                </div>
                <div className="walk-exp-control">
                    <span className="walk-exp-control-label">{t("walk_exp_tiers")}</span>
                    <div className="walk-exp-chips">
                        {(["low", "high"] as const).map(tk => {
                            const on = tiers.includes(tk);
                            return (
                                <button
                                    key={tk}
                                    type="button"
                                    aria-pressed={on}
                                    disabled={pending === `tier-${tk}` || readOnly}
                                    className={`walk-exp-chip${on ? " walk-exp-chip--on" : ""}`}
                                    onClick={() => toggleTier(tk, !on)}
                                >
                                    {t(`wlayout_tier_${tk}`)}
                                </button>
                            );
                        })}
                    </div>
                    <span className="walk-exp-control-hint">{t("walk_exp_tiers_hint")}</span>
                </div>
                <div className="walk-exp-control">
                    <span className="walk-exp-control-label">{t("walk_experiment_ends_at")}</span>
                    <input
                        type="date"
                        className="walk-exp-date"
                        value={toDateInput(endsAt)}
                        disabled={pending === "ends"}
                        onChange={(e) => {
                            const v = e.target.value;
                            void patch({
                                picking_walk_experiment_ends_at: v ? new Date(`${v}T23:59:59`).toISOString() : null,
                            }, "ends");
                        }}
                    />
                    <span className="walk-exp-control-hint">{t("walk_experiment_ends_at_hint")}</span>
                </div>
            </div>
            </Section>

            {/* ── Which arm is faster: estimated vs measured, ranked ───── */}
            <div className="walk-exp-analysis">
                <h5 className="walk-exp-subhead">{t("walk_exp_sec_analysis")}</h5>
                {results?.estimates_measured_at && results?.layout_updated_at
                    && new Date(results.layout_updated_at).getTime()
                        > new Date(results.estimates_measured_at).getTime() && (
                    <p className="walk-exp-control-hint walk-exp-stale-estimates">
                        {t("walk_exp_estimates_stale")}
                    </p>
                )}
                {!loaded && <div className="admin-settings-empty">{t("loading")}</div>}
                {loaded && (results?.byPickType?.length ?? 0) === 0 && (
                    <div className="admin-settings-empty">{t("walk_experiment_no_data")}</div>
                )}
                {(results?.byPickType ?? []).map((p) => {
                    const h = results?.headline?.find(x =>
                        x.pick_type === p.pick_type && (x.tier ?? "low") === (p.tier ?? "low"));
                    // Fastest first; arms with no measurement sink to the end
                    // in enrolment order rather than pretending to rank.
                    const ranked = [...p.arms].sort((a, b) => {
                        if (a.median_s_per_move === null) return b.median_s_per_move === null ? 0 : 1;
                        if (b.median_s_per_move === null) return -1;
                        return a.median_s_per_move - b.median_s_per_move;
                    });
                    const fastest = ranked.find(a => a.median_s_per_move !== null)?.arm ?? null;
                    return (
                        <div key={`${p.pick_type}-${p.tier ?? "low"}`} className="walk-exp-analysis-group">
                            <div className="walk-exp-headline-type">
                                {t(PICK_TYPES.find(x => x.value === p.pick_type)?.labelKey ?? p.pick_type)}
                                {(p.tier ?? "low") === "high" && ` · ${t("wlayout_tier_high")}`}
                                <span className="walk-exp-baseline-note">
                                    {t("walk_experiment_vs_baseline").replace("{arm}", labelOf(p.baseline_arm))}
                                </span>
                            </div>
                            <div className="admin-settings-table admin-settings-table--6col walk-exp-table walk-exp-table--sm">
                                <div className="admin-settings-row admin-settings-row--head">
                                    <div>{t("walk_experiment_col_arm")}</div>
                                    <div>{t("walk_exp_col_estimated")}</div>
                                    <div>{t("walk_exp_col_measured")}</div>
                                    <div title={t("walk_experiment_col_move_hint")}>{t("walk_experiment_col_move")}</div>
                                    <div>{t("walk_experiment_col_waves")}</div>
                                    <div>{t("walk_exp_col_confidence")}</div>
                                </div>
                                {ranked.map((s) => {
                                    const est = results?.estimates?.[s.arm];
                                    const cmp = h?.comparisons.find(c => c.arm === s.arm);
                                    const isBaseline = s.arm === p.baseline_arm;
                                    return (
                                        <div className="admin-settings-row" key={s.arm}>
                                            <div className="admin-settings-warehouse">
                                                {labelOf(s.arm)}
                                                {s.arm === fastest && (
                                                    <span className="walk-exp-fastest-pill">{t("walk_exp_fastest")}</span>
                                                )}
                                                {isBaseline && (
                                                    <span className="walk-exp-variant-live">{t("walk_experiment_baseline")}</span>
                                                )}
                                            </div>
                                            <div title={est?.basis ?? ""}>
                                                {est
                                                    ? `${est.delta_pct > 0 ? "+" : est.delta_pct < 0 ? "−" : ""}${Math.abs(est.delta_pct).toFixed(1)}% ±${est.ci_pct.toFixed(1)}`
                                                    : "—"}
                                            </div>
                                            <div className={cmp?.delta_pct != null
                                                ? (cmp.delta_pct > 0 ? "walk-exp-delta--up" : "walk-exp-delta--down")
                                                : undefined}
                                            >
                                                {isBaseline
                                                    ? t("walk_experiment_baseline")
                                                    : cmp?.delta_pct != null
                                                        ? `${cmp.delta_pct > 0 ? "+" : "−"}${Math.abs(cmp.delta_pct).toFixed(1)}%`
                                                            + (cmp.ci95 ? ` (${cmp.ci95.low_pct.toFixed(1)}…${cmp.ci95.high_pct.toFixed(1)})` : "")
                                                        : "—"}
                                            </div>
                                            <div>{fmt1(s.median_s_per_move)}s</div>
                                            <div>{s.waves ?? 0}</div>
                                            <div>
                                                {isBaseline || !cmp
                                                    ? "—"
                                                    : t(`walk_experiment_confidence_${cmp.confidence}`)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {h?.multiple_comparisons && (
                                <p className="walk-exp-control-hint">{t("walk_experiment_multiple_note")}</p>
                            )}
                        </div>
                    );
                })}
                {loaded && (results?.byPickType?.length ?? 0) === 0 && results?.estimates
                    && Object.keys(results.estimates).length > 0 && (
                    <p className="walk-exp-control-hint">{t("walk_exp_estimates_only_hint")}</p>
                )}
            </div>

            {/* ── Detail table, including the control metric ───────────── */}
            {(results?.byPickType?.length ?? 0) > 0 && (
                <Section title={t("walk_exp_sec_detail")}>
                <div className="admin-settings-table admin-settings-table--6col walk-exp-table walk-exp-table--sm">
                    <div className="admin-settings-row admin-settings-row--head">
                        <div>{t("walk_experiment_col_arm")}</div>
                        <div>{t("walk_experiment_col_waves")}</div>
                        <div>{t("walk_experiment_col_units")}</div>
                        <div title={t("walk_experiment_col_move_hint")}>{t("walk_experiment_col_move")}</div>
                        <div title={t("walk_experiment_col_same_hint")}>{t("walk_experiment_col_same")}</div>
                        <div>{t("walk_experiment_col_uph")}</div>
                    </div>
                    {(results?.byPickType ?? []).flatMap(p => p.arms.map(s => (
                        <div className="admin-settings-row" key={`${p.pick_type}-${p.tier ?? "low"}-${s.arm}`}>
                            <div className="admin-settings-warehouse">
                                {t(PICK_TYPES.find(x => x.value === p.pick_type)?.labelKey ?? p.pick_type)}
                                {(p.tier ?? "low") === "high" && ` · ${t("wlayout_tier_high")}`}
                                {" · "}
                                {labelOf(s.arm)}
                                {p.baseline_arm === s.arm && (
                                    <span className="walk-exp-variant-live">{t("walk_experiment_baseline")}</span>
                                )}
                            </div>
                            <div>{s.waves ?? 0}</div>
                            <div>{s.units ?? 0}</div>
                            <div>{fmt1(s.median_s_per_move)}s</div>
                            <div>{fmt1(s.median_s_per_same_loc_pick)}s</div>
                            <div>{fmt0(s.units_per_hour)}</div>
                        </div>
                    )))}
                </div>
                </Section>
            )}

            {/* ── Per-wave feed — a wave that just finished lands here ─── */}
            {(results?.recentWaves?.length ?? 0) > 0 && (
                <Section
                    title={t("walk_experiment_recent_waves")}
                    meta={String(results?.recentWaves?.length ?? 0)}
                >
                    <div className="admin-settings-table admin-settings-table--5col walk-exp-table walk-exp-table--sm">
                        <div className="admin-settings-row admin-settings-row--head">
                            <div>{t("walk_experiment_col_wave")}</div>
                            <div>{t("walk_experiment_col_arm")}</div>
                            <div>{t("walk_experiment_col_units")}</div>
                            <div>{t("walk_experiment_col_move")}</div>
                            <div>{t("walk_experiment_col_uph")}</div>
                        </div>
                        {(results?.recentWaves ?? []).map(w => (
                            <div className="admin-settings-row" key={w.wave_id}>
                                <div className="admin-settings-warehouse">
                                    #{w.wave_id}
                                    <span className="walk-exp-wave-meta">
                                        {" "}{t(PICK_TYPES.find(x => x.value === w.pick_type)?.labelKey ?? w.pick_type)}
                                        {(w.tier ?? "low") === "high" && ` · ${t("wlayout_tier_high")}`}
                                        {w.completed_at ? ` · ${new Date(w.completed_at).toLocaleString()}` : ""}
                                    </span>
                                </div>
                                <div>
                                    <span className={`walk-exp-arm-pill walk-exp-arm-pill--${w.walk_pattern}`}>
                                        {labelOf(w.walk_pattern)}
                                    </span>
                                </div>
                                <div>{w.units}</div>
                                <div>{fmt1(w.median_s_per_move)}s</div>
                                <div>{fmt0(w.units_per_hour)}</div>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* ── Route map: the walk drawn over the layout plan ───────── */}
            <Section
                title={t("walk_exp_map_title")}
                meta={t("walk_exp_map_meta")}
                lazy
            >
                <RouteMap
                    warehouse={warehouse}
                    arms={[...BUILTIN, ...variants.map(v => v.code)]}
                    labelOf={labelOf}
                />
            </Section>

            {/* ── Corridor route preview — that pairing is inferred ────── */}
            <details
                className="walk-exp-preview"
                open={previewOpen}
                onToggle={(e) => {
                    const open = (e.currentTarget as HTMLDetailsElement).open;
                    setPreviewOpen(open);
                    if (open && !preview) void loadPreview();
                }}
            >
                <summary className="walk-exp-preview-summary">
                    <ChevronRight size={16} strokeWidth={2} />
                    {t("walk_experiment_preview")}
                </summary>
                <div className="walk-exp-preview-body">
                    <p className="admin-settings-section-hint">{t("walk_experiment_preview_hint")}</p>
                    {previewOpen && !preview && <div className="admin-settings-empty">{t("loading")}</div>}
                    {preview && (
                        <>
                            <p className="walk-exp-coverage">
                                {t("walk_experiment_coverage")
                                    .replace("{pct}", preview.stats.coverage_pct.toFixed(0))
                                    .replace("{reordered}", String(preview.stats.reordered))
                                    .replace("{total}", String(preview.stats.lowLocations))}
                                {preview.stats.ambiguous > 0 && (
                                    <> {t("walk_experiment_coverage_ambiguous")
                                        .replace("{n}", String(preview.stats.ambiguous))}</>
                                )}
                            </p>
                            {preview.samples.map((s, i) => (
                                <div className="walk-exp-stop" key={i}>
                                    <div className="walk-exp-stop-title">
                                        {s.bays.map(b => `${b.aisle ?? "?"}-${b.bay ?? "?"}`).join("  ↔  ")}
                                    </div>
                                    <div className="walk-exp-stop-cols">
                                        <div>
                                            <div className="walk-exp-stop-col-head">{t("walk_pattern_standard")}</div>
                                            {s.current.map(l => (
                                                <div key={`c-${l.location}`} className="walk-exp-stop-cell">
                                                    {l.aisle}-{l.bay}-{l.level}
                                                    <span className="walk-exp-stop-walk">{l.walk_order}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div>
                                            <div className="walk-exp-stop-col-head">{t("walk_pattern_corridor")}</div>
                                            {s.corridor.map(l => (
                                                <div key={`n-${l.location}`} className="walk-exp-stop-cell">
                                                    {l.aisle}-{l.bay}-{l.level}
                                                    <span className="walk-exp-stop-walk">{l.walk_order}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {preview.samples.length === 0 && (
                                <div className="admin-settings-empty">{t("walk_experiment_preview_none")}</div>
                            )}
                        </>
                    )}
                </div>
            </details>

            {/* ── Conclude ─────────────────────────────────────────────────
                The picker lists every arm, including the one already live —
                gating this on "there is comparison data" would trap a
                warehouse that had adopted an arm: with the experiment off and
                no fresh data there would be no way back. Reverting must
                always be possible. */}
            <div className="walk-exp-actions">
                <span className="walk-exp-control-label">{t("walk_experiment_adopt_label")}</span>
                <select
                    className="walk-exp-adopt-select"
                    value={row.picking_walk_pattern}
                    disabled={pending === "adopt"}
                    onChange={(e) => {
                        const arm = e.target.value;
                        if (arm !== row.picking_walk_pattern) adopt(arm);
                    }}
                >
                    {adoptable.map(arm => (
                        <option key={arm} value={arm}>{labelOf(arm)}</option>
                    ))}
                </select>
                {/* Advisory, not a gate — you can still switch, you just
                    would not be switching on evidence. */}
                {!hasComparison && (
                    <span className="walk-exp-control-hint">{t("walk_experiment_adopt_no_data")}</span>
                )}
            </div>
        </details>
    );
};

const WalkPatternExperiment = ({ rows, onSaved }: {
    rows: WalkExperimentSettings[];
    onSaved: (warehouse: string, patch: Partial<WalkExperimentSettings>) => void;
}) => {
    const { t } = useTranslation();
    if (rows.length === 0) {
        return <div className="admin-settings-empty">{t("no_warehouses")}</div>;
    }
    return (
        <div className="walk-exp-list">
            <p className="admin-settings-section-hint walk-exp-intro">
                <FlaskConical size={16} strokeWidth={1.75} /> {t("walk_experiment_intro")}
            </p>
            {rows.map(r => (
                <Experiment key={r.warehouse} row={r} onSaved={p => onSaved(r.warehouse, p)} />
            ))}
        </div>
    );
};

export default WalkPatternExperiment;
